import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { endpoints } from "./endpoints";
import { queryKeys } from "./keys";
import type { ApiEnvelope } from "./types";

export type NotificationType =
  | "ORDER_AWAITING_VENDOR_RESPONSE"
  | "ORDER_ACCEPTED_BY_VENDOR"
  | "ORDER_REJECTED_BY_VENDOR"
  | "ORDER_TIMED_OUT"
  | "ORDER_CANCELLED_BY_CUSTOMER"
  | "ORDER_PAYMENT_SUCCEEDED"
  | "ORDER_PAYMENT_FAILED"
  | "ORDER_PAYMENT_EXPIRED"
  | "ORDER_PAYMENT_REFUNDED"
  | "PAYMENT_CANCELLED"
  | "DUPLICATE_PAYMENT_REFUNDED"
  | "WALLET_TOPUP_SUCCEEDED"
  | "BID_SUBMITTED"
  | "BID_REVISED"
  | "BIDDING_CLOSED_NO_BIDS"
  /** Phase 6. Vendor-bound; listed so the union matches the API enum. */
  | "WORK_ORDERS_CREATED"
  | "WORK_ORDER_ASSIGNED"
  | "WORK_ORDER_COMPLETED"
  | "ORDER_COMPLETED";

export type NotificationEntityType = "Order" | "CustomerRequest" | "WalletTopUp" | "WorkOrder";

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  entityType: NotificationEntityType;
  entityId: string;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListParams extends Record<string, unknown> {
  page?: number;
  limit?: number;
}

export interface PaginatedNotifications {
  items: AppNotification[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

async function fetchNotifications(
  params?: NotificationListParams,
): Promise<PaginatedNotifications> {
  const { data } = await apiClient.get<ApiEnvelope<PaginatedNotifications>>(
    endpoints.notifications.list,
    { params },
  );
  return data.data;
}

async function markNotificationRead(notificationId: string): Promise<void> {
  await apiClient.patch(endpoints.notifications.markRead(notificationId));
}

export function useNotifications(params?: NotificationListParams) {
  return useQuery({
    queryKey: queryKeys.notifications.list(params),
    queryFn: () => fetchNotifications(params),
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all() });
    },
  });
}
